import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useBrand, BrandLogo } from "@/lib/brand";
import { Panel } from "@/components/Panel";
import { Loader2, Upload, Trash2, Palette, Check, X } from "lucide-react";
import { toast } from "sonner";

const SWATCHES = [
  { name: "Bloomberg Amber", color: "#FFB000" },
  { name: "Terminal Green", color: "#00E676" },
  { name: "Cyan", color: "#00BFFF" },
  { name: "Coral", color: "#FF6B5C" },
  { name: "Royal Blue", color: "#3B6FFF" },
  { name: "Magenta", color: "#FF38A6" },
  { name: "Slate", color: "#94A3B8" },
  { name: "White", color: "#F5F5F5" },
];

export default function BrandKitTab() {
  const { brand, refresh } = useBrand();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setForm({
      brand_name: brand.brand_name || "DARA",
      tagline: brand.tagline || "",
      accent_color: brand.accent_color || "#FFB000",
      email_from_name: brand.email_from_name || "",
      email_reply_to: brand.email_reply_to || "",
    });
  }, [brand]);

  if (!form) return <div className="text-zinc-500 font-mono text-[11px]">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Pydantic EmailStr rejects '' — omit empty optional fields so a clear
      // operation doesn't 422.
      const payload = { ...form };
      if (!payload.email_reply_to) delete payload.email_reply_to;
      if (!payload.tagline) delete payload.tagline;
      if (!payload.email_from_name) delete payload.email_from_name;
      await api.put("/brand-kit", payload);
      await refresh();
      toast.success("Brand kit saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/brand-kit/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the logo?")) return;
    try { await api.delete("/brand-kit/logo"); await refresh(); toast.success("Logo removed."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="space-y-6" data-testid="brand-kit-tab">
      <div>
        <h2 className="font-heading font-black text-2xl tracking-tight">Brand Kit</h2>
        <p className="font-mono text-[11px] text-zinc-500 mt-1">
          One source of truth that cascades across every customer-facing surface — login, share pages, ABM,
          deal rooms, embed widget, briefing emails. Skin everything in 60 seconds.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel title="// LOGO">
          <div className="p-5 space-y-4">
            <div className="border border-zinc-800 bg-black/40 h-24 flex items-center justify-center">
              <BrandLogo size="lg" />
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onFile} className="hidden" data-testid="brand-logo-file-input" />
            <div className="flex gap-2">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="flex-1 font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity" data-testid="brand-logo-upload-btn">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {brand.has_logo ? "REPLACE" : "UPLOAD"}
              </button>
              {brand.has_logo && (
                <button onClick={removeLogo} className="border border-zinc-800 text-zinc-400 font-mono text-[11px] uppercase tracking-widest px-3 py-2 hover:bg-zinc-900 hover:text-[#FF3B30]" data-testid="brand-logo-remove-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="font-mono text-[10px] text-zinc-500">PNG / SVG / JPG — max 4 MB. SVG recommended for sharpness.</div>
          </div>
        </Panel>

        <Panel title="// IDENTITY">
          <div className="p-5 space-y-3">
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">BRAND NAME</label>
              <input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white" data-testid="brand-name-input" />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">TAGLINE</label>
              <input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="One-line product positioning" className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white" data-testid="brand-tagline-input" />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">EMAIL FROM-NAME</label>
              <input value={form.email_from_name} onChange={(e) => set("email_from_name", e.target.value)} placeholder="DARA Insights" className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white" data-testid="brand-email-from-name" />
              <div className="font-mono text-[9px] text-zinc-600 mt-1">Shown in the inbox sender column on briefings, walkthroughs, deal-room notifications.</div>
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">REPLY-TO EMAIL (optional)</label>
              <input type="email" value={form.email_reply_to} onChange={(e) => set("email_reply_to", e.target.value)} placeholder="hello@dara.ai" className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white" data-testid="brand-email-reply-to" />
            </div>
          </div>
        </Panel>

        <Panel title="// ACCENT COLOR">
          <div className="p-5 space-y-3">
            <div className="border border-zinc-800 h-24 flex items-center justify-center" style={{ background: form.accent_color, color: form.accent_color === "#F5F5F5" || form.accent_color === "#FFB000" ? "#000" : "#FFF" }}>
              <span className="font-heading font-black text-2xl">Aa</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {SWATCHES.map((s) => (
                <button
                  key={s.color}
                  onClick={() => set("accent_color", s.color)}
                  className={`h-8 border-2 ${form.accent_color === s.color ? "border-white" : "border-zinc-900"} flex items-center justify-center transition`}
                  style={{ background: s.color }}
                  title={`${s.name} (${s.color})`}
                  data-testid={`brand-swatch-${s.color}`}
                >
                  {form.accent_color === s.color && <Check className="w-3.5 h-3.5" style={{ color: s.color === "#FFB000" || s.color === "#F5F5F5" ? "#000" : "#fff" }} />}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={form.accent_color}
                onChange={(e) => set("accent_color", e.target.value.toUpperCase())}
                className="flex-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white uppercase"
                placeholder="#FFB000"
                data-testid="brand-accent-input"
              />
              <input
                type="color"
                value={form.accent_color}
                onChange={(e) => set("accent_color", e.target.value.toUpperCase())}
                className="w-10 h-10 border-0 cursor-pointer"
                aria-label="Pick accent color"
              />
            </div>
            <div className="font-mono text-[9px] text-zinc-500 leading-relaxed">
              The accent flows into CSS variables `--brand-accent` and `--brand-accent-text`.
              Headings, CTAs, status pills, and chart highlights inherit it. Pre-existing yellow
              hex codes (#FFB000) in legacy components remain until they're migrated to the
              variable — full re-skin lands in a follow-up sprint.
            </div>
          </div>
        </Panel>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-2.5 hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-opacity" data-testid="brand-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          SAVE BRAND KIT
        </button>
      </div>
    </div>
  );
}
